<template>
    <Teleport to="body">
        <el-drawer v-model="drawerVisible" class="workbench-details-drawer" title="详情" size="55%" v-bind="$attrs">
            <div class="details-container">
                <div class="details-content">
                    <!-- 1. 资产总览 -->
                    <section class="details-section">
                        <h3 class="section-title">资产总览</h3>
                        <div class="section-table-wrap" v-loading="loadingAssetOverview">
                            <v-table density="compact" class="bg-transparent flex-grow-1 gm-table details-table"
                                style="width: 100%">
                                <thead>
                                    <tr>
                                        <th style="min-width: 150px">资产编号</th>
                                        <th style="min-width: 70px">能源类型</th>
                                        <th style="min-width: 100px">资产分类</th>
                                        <th style="min-width: 100px">资产名称</th>
                                        <th style="min-width: 100px">规格型号</th>
                                        <th style="width: 60px">单位</th>
                                        <th style="min-width: 90px">制造商</th>
                                        <th style="min-width: 90px">供应商</th>
                                        <th class="text-end" style="width: 80px">设备数量</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, idx) in assetOverviewPageData" :key="row.assetNumber ?? idx">
                                        <td>{{ row.assetNumber }}</td>
                                        <td>{{ row.energyType }}</td>
                                        <td>{{ row.assetType }}</td>
                                        <td>{{ row.assetName }}</td>
                                        <td>{{ row.model }}</td>
                                        <td>{{ row.unit }}</td>
                                        <td>{{ row.manufacturer }}</td>
                                        <td>{{ row.supplier }}</td>
                                        <td class="text-end">{{ row.equipmentNum }}</td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </div>
                        <div class="details-pagination" v-if="assetOverviewTotal > 0">
                            <TablePagination v-model="assetOverviewPage" :page-size="assetOverviewPageSize"
                                :total="assetOverviewTotal" />
                        </div>
                    </section>

                    <!-- 2. 收益分析 -->
                    <section class="details-section">
                        <h3 class="section-title">收益分析</h3>
                        <div class="section-table-wrap" v-loading="loadingRevenueAnalysis">
                            <v-table density="compact"
                                class="bg-transparent flex-grow-1 gm-table details-table revenue-table"
                                style="width: 100%">
                                <thead>
                                    <tr>
                                        <th class="revenue-table__col-name">电站名称</th>
                                        <th class="text-end revenue-table__col-num">装机容量(kWh)</th>
                                        <th class="text-end revenue-table__col-num">充电量(kWh)</th>
                                        <th class="text-end revenue-table__col-num">放电量(kWh)</th>
                                        <th class="text-end revenue-table__col-num">能量效率</th>
                                        <th class="text-end revenue-table__col-num">收益(元)</th>
                                        <th class="text-end revenue-table__col-unit-income">单位容量收益(元/kWh)</th>
                                        <th class="text-center revenue-table__col-status">运营状态</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, idx) in revenueAnalysisList" :key="(row.name ?? '') + '-' + idx"
                                        :class="{ 'row-abnormal': row.statusClass === 'abnormal' }">
                                        <td class="revenue-table__col-name">
                                            <span class="run-table-station-name run-table-station-name--revenue"
                                                :title="row.name">{{
                                                    row.name || '--'
                                                }}</span>
                                        </td>
                                        <td class="text-end">{{ row.capacity }}</td>
                                        <td class="text-end">{{ row.sumPositive }}</td>
                                        <td class="text-end">{{ row.sumReverse }}</td>
                                        <td class="text-end">{{ row.efficiency ?? '--' }}</td>
                                        <td class="text-end">{{ fmtIncome(row.sumIncome) }}</td>
                                        <td class="text-end">{{ row.unitCapacityIncome ?? '--' }}</td>
                                        <td class="text-center">
                                            <span class="status-tag" :class="row.opsStatus">{{ row.opsStatus ? '正常' :
                                                '异常' }}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </div>
                        <div class="details-pagination" v-if="revenueAnalysisTotal > 0">
                            <TablePagination v-model="revenueAnalysisPage" :page-size="revenueAnalysisPageSize"
                                :total="revenueAnalysisTotal" @change="fetchRevenueAnalysisData" />
                        </div>
                    </section>

                    <!-- 3. 收益(元) 图表 -->
                    <section class="details-section">
                        <h3 class="section-title">近30天收益（元）</h3>
                        <div class="section-chart-wrap" v-loading="loadingAssetOverview">
                            <VChart class="details-chart" :option="revenueChartOption" autoresize />
                        </div>
                    </section>

                    <!-- 4. 容量衰减分析 -->
                    <section class="details-section">
                        <h3 class="section-title">容量衰减分析</h3>
                        <div class="section-table-wrap" v-loading="loadingCapacityDecay">
                            <v-table density="compact" class="bg-transparent flex-grow-1 gm-table details-table"
                                style="width: 100%">
                                <thead>
                                    <tr>
                                        <th style="min-width: 120px">电站名称</th>
                                        <th style="width: 110px">投运日期</th>
                                        <th style="width: 130px">累计运行时长(h)</th>
                                        <th class="text-end" style="width: 120px">初始容量(kWh)</th>
                                        <th class="text-end" style="width: 120px">设备平均SOH(%)</th>
                                        <th class="text-end" style="width: 140px">设备平均衰减率(%)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, idx) in capacityDecayList" :key="(row.name ?? '') + '-' + idx">
                                        <td>
                                            <span class="run-table-station-name">{{ row.name || '--' }}</span>
                                        </td>
                                        <td>{{ row.commossioningTime }}</td>
                                        <td>{{ row.runningDays }}</td>
                                        <td class="text-end">{{ row.capacity ?? '--' }}</td>
                                        <td class="text-end">{{ row.avgSoh ?? '--' }}</td>
                                        <td class="text-end">{{ row.attenuationSoh ?? '--' }}</td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </div>
                        <div class="details-pagination" v-if="capacityDecayTotal > 0">
                            <TablePagination v-model="capacityDecayPage" :page-size="capacityDecayPageSize"
                                :total="capacityDecayTotal" @change="fetchCapacityDecayData" />
                        </div>
                    </section>
                    <section class="details-section alarm-section" v-loading="loadingAlarmAnalysis">
                        <h3 class="section-title">告警分析</h3>
                        <!-- 昨日告警 环形图 + 图例 -->
                        <div class="alarm-donut-row">
                            <div class="alarm-donut-wrap">
                                <VChart class="alarm-donut-chart" :option="alarmDonutOption" autoresize />
                            </div>
                            <ul class="alarm-donut-legend">
                                <li v-for="item in alarmDonutChartData" :key="item.name">
                                    <span class="dot" :style="{ background: item.color }"></span>
                                    {{ item.name }} {{ item.value }}条 <span class="pct">{{ alarmDonutPct(item)
                                    }}%</span>
                                </li>
                            </ul>
                        </div>

                        <!-- 各电站告警堆叠横向柱状图 + 图例 + 分页 -->
                        <div class="alarm-bar-block">
                            <div class="alarm-bar-legend">
                                <span><i class="sq sq-top1"></i>TOP1</span>
                                <span><i class="sq sq-top2"></i>TOP2</span>
                                <span><i class="sq sq-top3"></i>TOP3</span>
                                <span><i class="sq sq-other"></i>其他</span>
                            </div>
                            <div class="alarm-bar-chart-wrap" v-if="alarmBarHasData" :style="alarmBarChartWrapStyle">
                                <VChart class="alarm-bar-chart" :option="alarmBarOption" autoresize />
                            </div>
                            <div class="alarm-bar-empty" v-else>
                                暂无数据
                            </div>
                            <div class="alarm-bar-pagination" v-if="alarmBarHasData">
                                <span class="page-arrow" :class="{ disabled: alarmBarPage <= 1 }"
                                    @click="alarmBarPage = Math.max(1, alarmBarPage - 1)">&lt;</span>
                                <span v-for="(p, idx) in alarmBarPageDots" :key="idx" class="page-dot"
                                    :class="{ active: p === alarmBarPage, ellipsis: p === '...' }"
                                    @click="typeof p === 'number' && (alarmBarPage = p)">{{ typeof p === 'number' ? p :
                                        '···' }}</span>
                                <span class="page-arrow" :class="{ disabled: alarmBarPage >= alarmBarTotalPages }"
                                    @click="alarmBarPage = Math.min(alarmBarTotalPages, alarmBarPage + 1)">&gt;</span>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </el-drawer>
    </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import dayjs from 'dayjs'
import { getAssetOverview, getRevenueAnalysis, getCapacityDecayAnalysis, getAlarmAnalysis, getAlarmList } from '@/api/workbench/details'
import TablePagination from '@/components/TablePagination/index.vue'
import { fmtIncome } from '@/utils/fortmatter.js'

const props = defineProps({
    detailsVisible: {
        type: Boolean,
        default: false
    },
    task: {
        default: () => { },
        type: Object
    }
})

const emit = defineEmits(['update:detailsVisible'])

// 用计算属性桥接：关闭时通知父组件，确保父组件 detailsVisible 能同步关闭
const drawerVisible = computed({
    get: () => props.detailsVisible,
    set: (v) => emit('update:detailsVisible', v)
})

/** 详情各接口统计日：优先取待办/历史行上的日期（datetime），否则为「昨日」与现有逻辑一致 */
const detailsQueryDay = computed(() => {
    const task = props.task
    if (task && typeof task === 'object') {
        const raw = task.datetime ?? task.date ?? task.createTime
        if (raw != null && raw !== '') {
            const s = typeof raw === 'string' ? raw.trim() : raw
            if (s !== '' && s != null) {
                const d = dayjs(s)
                if (d.isValid()) return d
            }
        }
    }
    return dayjs().subtract(1, 'day')
})

/** 请求参数用：必须 isValid()，避免 format 成 Invalid Date 导致后端解析报 Text '' could not be parsed */
function safeDetailsQueryDay() {
    const d = detailsQueryDay.value
    if (d && typeof d.isValid === 'function' && d.isValid()) return d
    const fb = dayjs().subtract(1, 'day')
    return fb.isValid() ? fb : dayjs()
}

const loadingAssetOverview = ref(false)
const loadingRevenueAnalysis = ref(false)
const loadingCapacityDecay = ref(false)
const loadingAlarmAnalysis = ref(false)

// 资产总览
const assetOverviewList = ref([])
const assetOverviewPage = ref(1)
const assetOverviewPageSize = ref(5)
const assetOverviewTotal = computed(() => assetOverviewList.value.length)
const assetOverviewPageData = computed(() => {
    const start = (assetOverviewPage.value - 1) * assetOverviewPageSize.value
    return assetOverviewList.value.slice(start, start + assetOverviewPageSize.value)
})
const fetchAssetOverviewData = async () => {
    loadingAssetOverview.value = true
    try {
        const res = await getAssetOverview({
            // startTime: dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            // endTime: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            date: safeDetailsQueryDay().format('YYYY-MM-DD'),
        })
        if (res.code === 200 && res.data) {
            assetOverviewList.value = res.data.overview
            incomeList.value = res.data.incomeList
        }
    } finally {
        loadingAssetOverview.value = false
    }
}

const revenueAnalysisPage = ref(1)
const revenueAnalysisPageSize = ref(5)
const revenueAnalysisTotal = ref(0)
const revenueAnalysisList = ref([])
const fetchRevenueAnalysisData = async () => {
    loadingRevenueAnalysis.value = true
    try {
        const res = await getRevenueAnalysis({

            date: safeDetailsQueryDay().format('YYYY-MM-DD'),
            // startTime: dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            // endTime: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            pageNum: revenueAnalysisPage.value,
            pageSize: revenueAnalysisPageSize.value
        })
        if (res.code === 200 && res.rows) {
            revenueAnalysisList.value = res.rows
            revenueAnalysisTotal.value = res.total
        }
    } finally {
        loadingRevenueAnalysis.value = false
    }
}

const capacityDecayPage = ref(1)
const capacityDecayPageSize = ref(5)
const capacityDecayTotal = ref(0)
const capacityDecayList = ref([])
const fetchCapacityDecayData = async () => {
    loadingCapacityDecay.value = true
    try {
        const day = safeDetailsQueryDay()
        const res = await getCapacityDecayAnalysis({
            startTime: day.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            endTime: day.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            pageNum: capacityDecayPage.value,
            pageSize: capacityDecayPageSize.value
        })
        if (res.code === 200 && res.rows) {
            capacityDecayList.value = res.rows
            capacityDecayTotal.value = res.total
        }
    } finally {
        loadingCapacityDecay.value = false
    }
}

const alarmAnalysisList = ref([])
const fetchAlarmAnalysisData = async () => {
    const res = await getAlarmAnalysis({
        // startTime: dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        // endTime: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')
        date: safeDetailsQueryDay().format('YYYY-MM-DD'),
    })
    const data = res?.data
    alarmAnalysisList.value = Array.isArray(data) ? data : (data?.list ?? data ?? [])
}

const alarmList = ref([])

/** 是否为「其他」类汇总项（与具体告警码区分，避免参与 TOP 排序） */
function isOtherAlarmEntry(a) {
    const code = String(a?.code ?? '').trim()
    const desc = String(a?.description ?? '')
    if (code === '0') return true
    if (/其他/.test(desc)) return true
    return false
}

function alarmEntryCount(a) {
    const n = Number(a?.alarmCount ?? a?.count ?? a?.num ?? 0)
    return Number.isFinite(n) ? n : 0
}

/** 按接口 list：告警次数最高的三条为 TOP1～TOP3，其余明细 + 「其他」汇总 计入「其他」 */
function buildAlarmBarStationsFromRows(rows) {
    const safeRows = Array.isArray(rows) ? rows : []
    return safeRows.map((r) => {
        const list = Array.isArray(r?.list) ? r.list : []
        const otherEntries = []
        const specific = []
        for (const a of list) {
            if (isOtherAlarmEntry(a)) otherEntries.push(a)
            else specific.push(a)
        }
        specific.sort((a, b) => alarmEntryCount(b) - alarmEntryCount(a))
        const t1 = specific[0]
        const t2 = specific[1]
        const t3 = specific[2]
        const top1 = t1 ? alarmEntryCount(t1) : 0
        const top2 = t2 ? alarmEntryCount(t2) : 0
        const top3 = t3 ? alarmEntryCount(t3) : 0
        const restSpecific = specific.slice(3).reduce((s, x) => s + alarmEntryCount(x), 0)
        const otherBucket = otherEntries.reduce((s, x) => s + alarmEntryCount(x), 0)
        const other = restSpecific + otherBucket
        const sumParts = top1 + top2 + top3 + other
        const total = Number.isFinite(Number(r?.alarmTotal)) ? Number(r.alarmTotal) : sumParts
        return {
            name: r?.name ?? '--',
            stationId: r?.stationId,
            total,
            top1,
            top2,
            top3,
            other,
            top1Label: t1?.description || t1?.descriptionEn || '—',
            top2Label: t2?.description || t2?.descriptionEn || '—',
            top3Label: t3?.description || t3?.descriptionEn || '—',
            rawList: list
        }
    })
}

/**
 * queryAlarmList：常见为顶层 code/msg/total/rows（与截图一致），也可能包在 data 里。
 * 必须先识别 res.rows，避免 res.data 为 [] 等占位时误吞掉 rows。
 */
function normalizeAlarmListRows(res) {
    if (!res || typeof res !== 'object') return []
    if (Array.isArray(res.rows)) return res.rows
    const inner = res.data
    if (Array.isArray(inner)) return inner
    if (inner && typeof inner === 'object') {
        if (Array.isArray(inner.rows)) return inner.rows
        if (Array.isArray(inner.list)) return inner.list
        if (Array.isArray(inner.records)) return inner.records
    }
    if (Array.isArray(res.list)) return res.list
    return []
}

const fetchAlarmListData = async () => {
    const res = await getAlarmList({
        date: safeDetailsQueryDay().format('YYYY-MM-DD'),
        pageNum: 1,
        pageSize: 999
    })

    const rows = normalizeAlarmListRows(res)
    alarmList.value = rows
    alarmBarStations.value = buildAlarmBarStationsFromRows(rows)
    alarmBarPage.value = 1
}


function fetchAllDetailsData() {
    assetOverviewPage.value = 1
    revenueAnalysisPage.value = 1
    capacityDecayPage.value = 1
    loadingAlarmAnalysis.value = true
    Promise.allSettled([
        fetchAlarmListData(),
        fetchAlarmAnalysisData()
    ]).finally(() => {
        loadingAlarmAnalysis.value = false
    })
    fetchAssetOverviewData()
    fetchRevenueAnalysisData()
    fetchCapacityDecayData()
}

watch(
    () => drawerVisible.value,
    (visible) => {
        if (!visible) return
        fetchAllDetailsData()
    },
    { immediate: true }
)

watch(
    () => props.task,
    () => {
        if (!drawerVisible.value) return
        fetchAllDetailsData()
    },
    { deep: true }
)

// 昨日告警环形图：按接口返回格式解析
// 当前接口返回：{ safety, income, device, other }
// 同时兼容旧字段：revenue/equipment 以及中文字段
const ALARM_DONUT_MAP = [
    { keys: ['safety', '安全', '安全类', '安全类告警', 1], name: '安全类告警', color: '#E85D75' },
    { keys: ['income', 'revenue', '收益', '收益类', '收益类告警', 2], name: '收益类告警', color: '#FFB74D' },
    { keys: ['device', 'equipment', '设备', '设备类', '设备类告警', 3], name: '设备类告警', color: '#00C853' },
    { keys: ['other', '其他', '其他类', '其他类告警', 4], name: '其他类告警', color: '#3A68FF' }
]
const alarmDonutChartData = computed(() => {
    const raw = alarmAnalysisList.value
    // 支持接口返回对象：{ safety, revenue, equipment, other } 或 { 安全类告警: n, ... }
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        return ALARM_DONUT_MAP.map(({ keys, name, color }) => {
            const value = keys.reduce((acc, k) => (acc != null ? acc : raw[k]), null)
            return { name, value: Number(value) || 0, color }
        })
    }
    return ALARM_DONUT_MAP.map(({ name, color }) => ({ name, value: 0, color }))
})
const alarmDonutTotal = computed(() => alarmDonutChartData.value.reduce((s, d) => s + d.value, 0))
// 保留两位小数（用于右侧环形图图例百分比展示）
const alarmDonutPct = (item) => {
    if (alarmDonutTotal.value <= 0) return 0
    const pct = (item.value / alarmDonutTotal.value) * 100
    return Math.round(pct * 100) / 100
}
const alarmDonutOption = computed(() => ({

    tooltip: { trigger: 'item', formatter: '{b}: {c}个 ({d}%)' },
    series: [{
        type: 'pie',
        radius: ['50%', '72%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        label: { show: true, position: 'outside', formatter: '{d}%', fontSize: 12 },
        labelLine: { show: true, length: 8, length2: 6 },
        data: alarmDonutChartData.value.map(({ name, value, color }) => ({
            value,
            name,
            itemStyle: { color }
        }))
    }]
}))

// 各电站告警堆叠横向柱状图：按每站 list 中 alarmCount 取 TOP1～TOP3，其余与「其他」类汇总 计入「其他」
const alarmBarStations = ref([])
const alarmBarPage = ref(1)
const alarmBarPageSize = 10
const alarmBarTotalPages = computed(() => Math.max(1, Math.ceil(alarmBarStations.value.length / alarmBarPageSize)))
watch(
    () => alarmBarTotalPages.value,
    (total) => {
        if (alarmBarPage.value > total) alarmBarPage.value = total
        if (alarmBarPage.value < 1) alarmBarPage.value = 1
    }
)
const alarmBarPageDots = computed(() => {
    const total = alarmBarTotalPages.value
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1)
    const page = alarmBarPage.value
    const set = new Set([1, total])
    if (page > 1) set.add(page)
    if (page > 2) set.add(page - 1)
    if (page < total) set.add(page + 1)
    const arr = []
    let last = 0
    for (const p of [...set].sort((a, b) => a - b)) {
        if (p > last + 1) arr.push('...')
        arr.push(p)
        last = p
    }
    return arr
})
const alarmBarPageData = computed(() => {
    const start = (alarmBarPage.value - 1) * alarmBarPageSize
    return alarmBarStations.value.slice(start, start + alarmBarPageSize)
})
const alarmBarHasData = computed(() => alarmBarPageData.value.length > 0)
/** 横向柱条数随当前页条数变化，用固定高度避免 ECharts 在 0 高度内不绘制 */
const alarmBarChartWrapStyle = computed(() => {
    const n = alarmBarPageData.value.length
    const h = Math.min(720, Math.max(280, 32 + n * 58))
    return { '--alarm-bar-chart-h': `${h}px` }
})
const alarmBarOption = computed(() => {
    const list = alarmBarPageData.value
    const yData = list.map(s => `${s.name ?? '--'}\n告警总数 ${s.total}`)
    return {
        grid: { top: 12, bottom: 12, left: 8, right: 80, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter(params) {
                if (!params?.length) return ''
                const idx = params[0].dataIndex
                const s = list[idx]
                if (!s) return ''
                const esc = (t) => String(t ?? '').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                const lines = [
                    `<div style="font-weight:600">${esc(s.name)}</div><div style="opacity:.85;font-size:12px;margin-bottom:6px">告警总数 ${s.total}</div>`,
                    `<span style="color:#0047EB">●</span> TOP1 ${esc(s.top1Label)}：<b>${s.top1}</b>`,
                    `<span style="color:#165DFF">●</span> TOP2 ${esc(s.top2Label)}：<b>${s.top2}</b>`,
                    `<span style="color:#6AA1FF">●</span> TOP3 ${esc(s.top3Label)}：<b>${s.top3}</b>`,
                    `<span style="color:#BEDAFF">●</span> 其他：<b>${s.other}</b>`
                ]
                return lines.join('<br/>')
            }
        },
        xAxis: { type: 'value', max: 'dataMax', splitLine: { lineStyle: { color: 'rgba(0,0,0,0.06)' } }, axisLabel: { fontSize: 11 }, axisLine: { show: false }, axisTick: { show: false } },
        yAxis: {
            type: 'category',
            data: yData,
            axisLabel: { fontSize: 12, color: '#2e2e2e', lineHeight: 18 },
            axisLine: { show: false },
            axisTick: { show: false },
            inverse: true
        },
        series: [
            { name: 'TOP1', type: 'bar', stack: 'alarm', barWidth: 14, data: list.map(s => s.top1), itemStyle: { color: '#0047EB' } },
            { name: 'TOP2', type: 'bar', stack: 'alarm', barWidth: 14, data: list.map(s => s.top2), itemStyle: { color: '#165DFF' } },
            { name: 'TOP3', type: 'bar', stack: 'alarm', barWidth: 14, data: list.map(s => s.top3), itemStyle: { color: '#6AA1FF' } },
            { name: '其他', type: 'bar', stack: 'alarm', barWidth: 14, data: list.map(s => s.other), itemStyle: { color: '#BEDAFF' } }
        ]
    }
})

// 收益(元) 堆叠柱状图：配置驱动，避免重复
const REVENUE_SERIES = [
    { name: '峰谷套利', field: 'income', color: '#3A68FF' },
    { name: '虚拟电厂', field: 'vppIncome', color: '#00C853' },
    { name: '电力现货', field: 'esmIncome', color: '#FFB74D' },
    { name: '其他收益', field: 'otherIncome', color: '#B8860B' }
]
const incomeList = ref([])

/** 本月收益图横轴：只展示月、日（MM-DD），不展示年 */
function formatIncomeChartAxisDate(dateVal) {
    if (dateVal == null || dateVal === '') return ''
    const d = dayjs(dateVal)
    return d.isValid() ? d.format('MM-DD') : String(dateVal)
}

/** 按堆叠总和最大值选择 Y 轴量级：元 / 万元 / 亿元 */
function pickRevenueChartScale(maxStackYuan) {
    const n = Math.abs(Number(maxStackYuan)) || 0
    if (n >= 1e8) return { divisor: 1e8, unitName: '亿元' }
    if (n >= 1e4) return { divisor: 1e4, unitName: '万元' }
    return { divisor: 1, unitName: '元' }
}

const revenueChartOption = computed(() => {
    const list = incomeList.value

    let maxStack = 0
    for (const item of list) {
        let sum = 0
        for (const { field } of REVENUE_SERIES) {
            sum += Number(item[field] ?? 0) || 0
        }
        if (sum > maxStack) maxStack = sum
    }
    const scale = pickRevenueChartScale(maxStack)
    const d = scale.divisor

    return {
        grid: { top: 40, bottom: 28, left: 50, right: 20, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            backgroundColor: 'rgba(255,255,255,0.95)',
            borderColor: 'transparent',
            borderRadius: 8,
            padding: [10, 14],
            textStyle: { fontSize: 12, color: '#333' },
            formatter(params) {
                const unit = scale.unitName
                const rows = Array.isArray(params) ? params : [params]
                const title = rows[0]?.axisValueLabel ?? rows[0]?.name ?? ''
                const lines = rows.map((p) => {
                    const raw = p.value
                    const val =
                        typeof raw === 'object' && raw != null && 'value' in raw ? raw.value : raw
                    const v = Number(val)
                    let txt
                    if (!Number.isFinite(v)) {
                        txt = String(raw)
                    } else if (d === 1) {
                        txt = Number.isInteger(v)
                            ? String(v)
                            : v.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                    } else {
                        txt = v.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    }
                    return `${p.marker}${p.seriesName}: ${txt} ${unit}`
                })
                const body = lines.join('<br/>')
                return title ? `${title}<br/>${body}` : body
            }
        },
        legend: {
            data: REVENUE_SERIES.map(s => s.name),
            top: 0,
            right: 0,
            itemWidth: 12,
            itemHeight: 12,
            itemGap: 16,
            textStyle: { fontSize: 12, color: 'rgba(46,46,46,0.85)' }
        },
        xAxis: {
            type: 'category',
            data: list.map((item) => formatIncomeChartAxisDate(item.date)),
            axisLabel: { fontSize: 11, color: 'rgba(46,46,46,0.6)' },
            axisLine: { lineStyle: { color: 'rgba(0,0,0,0.08)' } },
            axisTick: { show: false }
        },
        yAxis: {
            type: 'value',
            name: scale.unitName,
            nameLocation: 'end',
            axisLabel: {
                fontSize: 11,
                color: 'rgba(46,46,46,0.6)',
                formatter: (val) => {
                    const n = Number(val)
                    if (!Number.isFinite(n)) return String(val)
                    if (d === 1) return Number.isInteger(n) ? String(n) : n.toFixed(2)
                    return n.toFixed(2)
                }
            },
            splitLine: { lineStyle: { color: 'rgba(0,0,0,0.06)' } },
            axisLine: { show: false },
            axisTick: { show: false }
        },
        series: REVENUE_SERIES.map(({ name, field, color }) => ({
            name,
            type: 'bar',
            stack: 'total',
            barWidth: 18,
            data: list.map((item) => ({
                value: (Number(item[field] ?? 0) || 0) / d
            })),
            itemStyle: { color }
        }))
    }
})
</script>

<style lang="scss" scoped>
/* 详情抽屉：禁止横向滚动条，纵向仍由抽屉 body 滚动 */
:deep(.workbench-details-drawer .el-drawer__body) {
    overflow-x: hidden;
    overflow-y: auto;
    box-sizing: border-box;
}

.details-container {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    min-height: 100%;
    background-color: #fff;
    overflow-x: hidden;
    box-sizing: border-box;
}

.details-content {
    padding: 0 20px 24px;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
}

.details-section {
    margin-bottom: 24px;

    &:last-child {
        margin-bottom: 0;
    }
}

.section-title {
    margin: 0 0 12px 0;
    font-size: 15px;
    font-weight: 600;
    color: #2e2e2e;
    font-family: "HarmonyOS Sans SC", sans-serif;
}

.section-table-wrap {
    overflow-x: hidden;
    overflow-y: visible;
    max-width: 100%;
    min-width: 0;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-sizing: border-box;

    /* v-table 默认 wrapper 可能带横向滚动，此处与业务一致：不显示表区域内滚动条 */
    :deep(.v-table__wrapper) {
        overflow-x: hidden;
        overflow-y: visible;
        max-width: 100%;
    }
}

/* 收益分析：固定表格布局，压缩「电站名称」列宽，避免撑出抽屉横向滚动 */
.details-table.revenue-table {
    :deep(table) {
        table-layout: fixed;
        width: 100%;
    }

    :deep(thead th.revenue-table__col-name),
    :deep(tbody td.revenue-table__col-name) {

        width: 198px;
        min-width: 0;
        box-sizing: border-box;
        vertical-align: middle;
    }

    :deep(.revenue-table__col-num) {
        width: 10%;
        min-width: 0;
    }

    :deep(.revenue-table__col-unit-income) {
        width: 18%;
        min-width: 0;
    }

    :deep(.revenue-table__col-status) {
        width: 72px;
        min-width: 0;
    }
}

.details-pagination {
    margin-top: 10px;
    width: 100%;

    :deep(.table-pagination) {
        width: 100%;
    }
}

.section-chart-wrap {
    height: 260px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

.details-chart {
    width: 100%;
    height: 100%;
}

.details-table {
    font-size: 13px;

    :deep(table) {
        border-collapse: collapse;
        width: 100%;
    }

    :deep(thead th),
    :deep(tbody td) {
        padding: 8px 12px !important;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }

    :deep(thead th) {
        background: #f5f7fa;
        color: rgba(46, 46, 46, 0.85);
        font-weight: 500;
        white-space: nowrap;
    }

    :deep(tbody tr:hover td) {
        background: rgba(58, 104, 255, 0.06) !important;
    }

    :deep(tbody tr.row-abnormal td) {
        background: rgba(33, 150, 243, 0.08) !important;
    }

    :deep(tbody tr) {
        .status-tag.normal {
            color: #2e2e2e;
        }

        .status-tag.abnormal {
            padding: 2px 8px;
            border-radius: 4px;
            background: rgba(33, 150, 243, 0.15);
            color: #1976d2;
        }
    }
}

/* 容量衰减分析 - 昨日告警环形图 */
.alarm-donut-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 40px;
    margin-top: 20px;
    padding: 16px 0;
}

.alarm-donut-wrap {
    position: relative;
    width: 220px;
    height: 200px;
    flex-shrink: 0;
    margin-left: 0;
}

.alarm-donut-chart {
    width: 100%;
    height: 100%;
}

.alarm-donut-center {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size: 14px;
    font-weight: 500;
    color: #2e2e2e;
    white-space: nowrap;
}

.alarm-donut-legend {
    list-style: none;
    margin: 0;
    padding: 0;
    width: auto;
    max-width: none;
    display: block;
    font-size: 13px;
    color: rgba(46, 46, 46, 0.85);

    li {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 10px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    .dot-safety {
        background: #E85D75;
    }

    .dot-revenue {
        background: #FFB74D;
    }

    .dot-equipment {
        background: #00C853;
    }

    .dot-other {
        background: #3A68FF;
    }

    .pct {
        margin-left: 4px;
        color: rgba(46, 46, 46, 0.65);
    }
}

/* 各电站告警堆叠横向柱状图 */
.alarm-bar-block {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.alarm-bar-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 16px 24px;
    margin-bottom: 12px;
    font-size: 12px;
    color: rgba(46, 46, 46, 0.8);

    .sq {
        display: inline-block;
        width: 10px;
        height: 10px;
        margin-right: 6px;
        vertical-align: middle;
    }

    .sq-top1 {
        background: #0047EB;
    }

    .sq-top2 {
        background: #165DFF;
    }

    .sq-top3 {
        background: #6AA1FF;
    }

    .sq-other {
        background: #BEDAFF;
    }
}

/* 勿用 height:4%，父级无高度时百分比为 0，会导致 ECharts 画布高度为 0 只见空白 */
.alarm-bar-chart-wrap {
    min-height: 280px;
    height: var(--alarm-bar-chart-h, 360px);
    border-radius: 8px;
    overflow: hidden;
}

.alarm-bar-chart {
    width: 100%;
    min-height: 260px;
    height: 100%;
}

.alarm-bar-empty {
    height: 420px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(46, 46, 46, 0.5);
    font-size: 14px;
}

.alarm-bar-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 16px;

    .page-arrow {
        cursor: pointer;
        font-size: 14px;
        color: #2e2e2e;
        padding: 4px 8px;
        user-select: none;

        &.disabled {
            color: #ccc;
            cursor: not-allowed;
        }
    }

    .page-dot {
        width: 24px;
        height: 24px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        color: rgba(46, 46, 46, 0.6);
        cursor: pointer;
        border-radius: 50%;
        border: 1px solid transparent;

        &.ellipsis {
            cursor: default;
        }

        &.active {
            background: #3A68FF;
            color: #fff;
            border-color: #3A68FF;
        }
    }
}

.run-table-station-name {
    color: #2E2E2E;
    font-weight: 500;
    margin-bottom: 1px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 200px;
}

.run-table-station-name--revenue {
    width: 100%;
    max-width: 100%;
    display: block;
    box-sizing: border-box;
}

/* 小屏幕适配：表格增加横向滚动条 */
@media (max-width: 1400px),
(max-height: 800px) {
    .section-table-wrap {
        overflow-x: auto;
        overflow-y: visible;
        max-width: 100%;
        scrollbar-gutter: stable;

        :deep(.v-table__wrapper) {
            overflow-x: auto;
            overflow-y: visible;
        }

        :deep(table) {
            min-width: 900px;
        }
    }

    .details-table {
        :deep(table) {
            min-width: 900px;
        }
    }

    .details-table.revenue-table {
        :deep(table) {
            min-width: 1000px;
        }
    }
}

@media (min-width: 1401px) and (max-width: 1919px) {
    .section-table-wrap {
        overflow-x: auto;
        overflow-y: visible;
        max-width: 100%;
        scrollbar-gutter: stable;

        :deep(.v-table__wrapper) {
            overflow-x: auto;
            overflow-y: visible;
        }

        :deep(table) {
            min-width: 1000px;
        }
    }

    .details-table {
        :deep(table) {
            min-width: 1000px;
        }
    }

    .details-table.revenue-table {
        :deep(table) {
            min-width: 1100px;
        }
    }
}
</style>
